/**
 * Finance › Reports (FINANCE.md §8.2's Reports tab): the statements as CSV files — the P&L by
 * month and line, the balance sheet by account, actual against budget — each rendered by the
 * API from the same JSON the screens read, so a download can never disagree with its screen.
 */

import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type Property } from '../../api.js';
import { EntityPicker, useEntities, useEntityId } from '../../components/EntityPicker.js';

function monthsAgo(n: number): string {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() - n);
  return d.toISOString().slice(0, 7);
}

export function ReportsPage(): ReactNode {
  const entityId = useEntityId();
  const entities = useEntities();
  const [from, setFrom] = useState(() => monthsAgo(11));
  const [to, setTo] = useState(() => monthsAgo(0));
  const [propertyId, setPropertyId] = useState<string>('');

  const properties = useQuery({
    queryKey: ['properties'],
    queryFn: () => api.get<{ properties: Property[] }>('/api/properties'),
  });

  const noEntities = entities.data !== undefined && entities.data.entities.length === 0;
  const range = `entity=${entityId}&from=${from}&to=${to}`;
  const property = propertyId === '' ? '' : `&property=${propertyId}`;
  const valid = entityId !== null && from <= to;

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Reports</h1>
          <p className="subtitle">
            The statements as files for a spreadsheet or an accountant. Each is rendered from the
            same figures its screen shows — code&apos;s, to the cent — with dollars in the cells.
          </p>
        </div>
      </header>

      <div className="filters">
        <EntityPicker />
        <label>
          From{' '}
          <input type="month" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          To <input type="month" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label>
          Property{' '}
          <select value={propertyId} onChange={(e) => setPropertyId(e.target.value)}>
            <option value="">Whole entity</option>
            {(properties.data?.properties ?? [])
              .filter((p) => p.entity_id === entityId)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </label>
      </div>

      {noEntities && (
        <div className="card empty">
          <h2>No entities.</h2>
          <p>
            Run <code>pnpm seed</code> to load the fictional sample portfolio.
          </p>
        </div>
      )}

      {valid && (
        <section className="card">
          <h2>Download</h2>
          <ul>
            <li>
              <a href={`/api/finance/reports/pnl.csv?${range}${property}`} download>
                Profit and loss, {from} to {to}
                {propertyId !== '' ? ', one property' : ''}
              </a>
              <span className="muted small">
                {' '}
                · by month and canonical line, with NOI, net and the pending lines named
              </span>
            </li>
            <li>
              <a href={`/api/finance/reports/balance-sheet.csv?entity=${entityId}`} download>
                Balance sheet, as QuickBooks last reported it
              </a>
              <span className="muted small">
                {' '}
                · by account, liabilities and equity as a person reads them, totals and the unclosed
                net income; the entity&apos;s, not a property&apos;s
              </span>
            </li>
            <li>
              <a href={`/api/finance/reports/budget.csv?${range}${property}`} download>
                Actual against budget, {from} to {to}
                {propertyId !== '' ? ', one property' : ''}
              </a>
              <span className="muted small">
                {' '}
                · by month and line: budget, actual, variance (positive favourable), NOI
              </span>
            </li>
          </ul>
          <p className="muted small">
            CSV, UTF-8, dollars with two decimals. The P&amp;L and budget files follow the property
            choice; the balance sheet is always the entity&apos;s.
          </p>
        </section>
      )}
    </div>
  );
}
